/**
 * Helpers purs pour les textes riches d’organigramme (sans DOM / DOMPurify).
 */

export const ORG_RICH_TEXT_VISIBLE_MAX = 1200
export const ORG_RICH_TEXT_HTML_MAX = 12_000

const HTML_MARKUP_RE = /<\/?(p|br|strong|b|em|i|u|s|strike|del|ul|ol|li|a|blockquote|h2|h3)\b/i

export function looksLikeHtml(value: string): boolean {
  return HTML_MARKUP_RE.test(value)
}

export function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/&#x[aA]0;/g, ' ')
    .replace(/&#(\d+);/g, (_match, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_match, hex) => String.fromCharCode(Number.parseInt(hex, 16)))
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
}

export function stripHtmlToText(value: string): string {
  if (!value) {
    return ''
  }
  const withBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|h2|h3|li|blockquote)>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
  return decodeHtmlEntities(withBreaks)
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function visibleTextLength(value: string): number {
  return stripHtmlToText(value).length
}

/**
 * Convertit les retours à la ligne des nœuds texte en `<br>`.
 * Ignore les sauts « pretty-print » entre balises (`</p>\n<p>`).
 */
export function htmlTextNewlinesToBr(html: string): string {
  return html.replace(/(<[^>]+>)|([^<]+)/g, (chunk, tag?: string, text?: string) => {
    if (tag) {
      return tag
    }
    const node = text ?? chunk
    if (!/[\n\r]/.test(node)) {
      return node
    }
    if (/^\s*$/.test(node)) {
      return ''
    }
    return node.replace(/\r\n|\r|\n/g, '<br>')
  })
}

export function plainTextToHtml(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }
  if (looksLikeHtml(trimmed)) {
    return htmlTextNewlinesToBr(trimmed)
  }
  const escaped = escapeHtml(trimmed)
  return escaped
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replaceAll('\n', '<br>')}</p>`)
    .join('')
}

export function normalizeOrgRichTextInput(value: string | null | undefined): string {
  if (value == null) {
    return ''
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }
  if (looksLikeHtml(trimmed)) {
    return htmlTextNewlinesToBr(trimmed).replace(/<p>\s*<\/p>/gi, '<p><br></p>')
  }
  return plainTextToHtml(trimmed)
}

export function isEmptyOrgRichText(value: string | null | undefined): boolean {
  if (!value) {
    return true
  }
  return stripHtmlToText(value).length === 0
}
